import { t } from '@lingui/core/macro';
import { useStytchB2BClient, type DiscoveredOrganization } from '@stytch/react/b2b';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router';

import type { DiscoveryInfo } from '@/app/sign-in/type';
import { GlobalLoader } from '@/components/GlobalLoader';
import { PageSpinner } from '@/components/loaders';
import { FullPage } from '@/components/page';
import { sessionDurationMinutes } from '@/constants/numbers';
import AuthError, { AUTH_ERROR_TYPE } from '@/error/AuthError';
import { isBusinessEmail } from '@/helper/validator';
import useLocationState from '@/hooks/useLocationState';
import { ROUTES } from '@/router';

const createSimpleError = (type: AUTH_ERROR_TYPE, email: string) => {
  return new AuthError({ type, details: { email } });
};

const getLastVisitedOrgId = (discoveredOrgs: DiscoveredOrganization[]) => {
  let lastVisitedTime;
  let lastVisitedOrgId;

  for (const org of discoveredOrgs) {
    const orgLastVisitedTime = org.membership.member?.untrusted_metadata?.lastAccessTime as
      | string
      | undefined;
    const date = orgLastVisitedTime != null && dayjs(orgLastVisitedTime);
    if (date) {
      if (org.member_authenticated && (!lastVisitedTime || date.isAfter(lastVisitedTime))) {
        lastVisitedTime = date;
        lastVisitedOrgId = org.organization.organization_id;
      }
    }
  }

  return lastVisitedOrgId;
};

/**
 * Redirected to this page from `/sign-in` if the user belongs to one or more organizations. Here
 * we will display a list of organizations the user belongs to and allow them to choose one. When
 * this happens, we will exchange the intermediate session token for a session token and redirect
 * them to the app.
 *
 * If the user only belongs to a single org, we will automatically exchange the intermediate
 * session token and redirect them to the app.
 *
 * If the user belongs to no orgs, we will redirect them to the configure org page.
 */
const DiscoverOrgs = () => {
  const state = useLocationState<DiscoveryInfo>();
  const stytch = useStytchB2BClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authError, setAuthError] = useState<AuthError | null>(null);
  const exchangedToken = useRef(false);

  const { discoveredOrgs, email, isDiscoverySignUp } = state;

  const needsToConfigureOrg = !!(discoveredOrgs?.length === 0);
  const belongsToOneOrg = !!(discoveredOrgs?.length === 1);
  const belongsToMultipleOrgs = !!(discoveredOrgs && discoveredOrgs.length > 1);

  let lastAccessedOrgId: string | undefined;

  // If the user belongs to multiple orgs, we will use the last accessed instance id from the
  // discovered orgs to determine which org to redirect to.
  if (belongsToMultipleOrgs) {
    lastAccessedOrgId = getLastVisitedOrgId(discoveredOrgs);
  }

  // If we didn't find a last accessed instance id on the member, we'll just use the first org in
  // the list.
  if ((belongsToOneOrg || belongsToMultipleOrgs) && !lastAccessedOrgId) {
    lastAccessedOrgId = discoveredOrgs[0].organization.organization_id;
  }

  const exchangeIntermediateSession = useCallback(
    async (orgId: string) => {
      try {
        exchangedToken.current = true;
        const { member } = await stytch.discovery.intermediateSessions.exchange({
          organization_id: orgId,
          session_duration_minutes: sessionDurationMinutes,
        });

        if (member) {
          await stytch.organization.members.update({
            member_id: member.member_id,
            untrusted_metadata: {
              lastAccessTime: dayjs().toISOString(),
            },
          });
        }

        setIsAuthenticated(true);
      } catch {
        setAuthError(createSimpleError(AUTH_ERROR_TYPE.UNKNOWN, email));
      }
    },
    [stytch, email],
  );

  useEffect(() => {
    // Do we need to also "&& discoveredOrgs[0].member_authenticated" here?
    if (
      !exchangedToken.current &&
      lastAccessedOrgId &&
      discoveredOrgs &&
      discoveredOrgs.length > 0
    ) {
      exchangeIntermediateSession(lastAccessedOrgId);
    }
  }, [lastAccessedOrgId, discoveredOrgs, exchangeIntermediateSession]);

  // This can only be undefined if the user typed this route in manually into their browser.
  if (!discoveredOrgs) {
    return <Navigate to={ROUTES.signIn.path} replace />;
  }

  if (needsToConfigureOrg) {
    if (isDiscoverySignUp && isBusinessEmail(email)) {
      // If the user doesn't belong to an org and came here from sign-up, take them to go
      // configure one
      return <Navigate to={ROUTES.configureOrg.path} state={{ ...state }} replace />;
    }

    const configureOrgError = isDiscoverySignUp
      ? createSimpleError(AUTH_ERROR_TYPE.CANNOT_SIGN_UP_WITH_PERSONAL_EMAIL, email)
      : createSimpleError(AUTH_ERROR_TYPE.CANNOT_CREATE_ORG_FROM_SIGN_IN, email);

    return <Navigate to={ROUTES.signIn.path} state={{ error: configureOrgError }} replace />;
  }

  if (authError) {
    return <Navigate to={ROUTES.signIn.path} state={{ error: authError }} replace />;
  }

  if (isAuthenticated) {
    if (state.redirectUrl) {
      return <Navigate to={state.redirectUrl} replace />;
    }
    return <Navigate to={ROUTES.app.path} replace />;
  }

  return (
    <FullPage title={t`Sign In`}>
      {needsToConfigureOrg && <PageSpinner />}
      {lastAccessedOrgId && <GlobalLoader />}
    </FullPage>
  );
};

export default DiscoverOrgs;
